import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, mergeMap, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { Settings } from '../../core/settings';
import { CommonLinkInfo, CommonLinksInfoRealModel } from './common-link-info';
import { ActivityLogger } from '../../activities/activity-logger';
import { comparators } from '../../core/comparators';

@Injectable()
export class CommonLinksInfoStorage {
    private readonly commonLinksInfo: Observable<CommonLinkInfo[]>;
    private loadAll$ = new BehaviorSubject<boolean>(true);

    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
        private readonly activityLogger: ActivityLogger,
    ) {
        this.commonLinksInfo = this.loadAll$
            .pipe(
                mergeMap(() => of(of(null), this.loadAll())),
                switchMap(x => x),
                publishReplay(1),
                refCount()
            );
    }

    public get all(): Observable<CommonLinkInfo[]> {
        return this.commonLinksInfo;
    }

    public forceLoadAll() {
        this.loadAll$.next(true);
    }

    public post(commonLinkInfo: CommonLinkInfo): Promise<void> {
        this.activityLogger.logActivity('Create Common Link');

        return this.http.post<void>(`${this.settings.apiUrl}/admin/common-links`, commonLinkInfo).toPromise();
    }

    public put(commonLinkInfo: CommonLinkInfo): Promise<void> {
        this.activityLogger.logActivity('Edit Common Link');

        return this.http.put<void>(`${this.settings.apiUrl}/admin/common-links/${commonLinkInfo.commonLinkId}`, commonLinkInfo).toPromise();
    }

    public delete(commonLinkId: number): Promise<void> {
        this.activityLogger.logActivity('Delete Common Link');

        return this.http.delete<void>(`${this.settings.apiUrl}/admin/common-links/${commonLinkId}`).toPromise();
    }

    private loadAll(): Observable<CommonLinkInfo[]> {
        return this.http.get(`${this.settings.apiUrl}/admin/common-links`)
            .pipe(
                map(rawData => {
                    let commonLinks = deserialize(rawData, CommonLinksInfoRealModel).commonLinksInfo;
                    commonLinks = commonLinks.sort((a, b) => comparators.dateTimeComparator(b ? b.postedDate : null, a ? a.postedDate : null));
                    return commonLinks;
                }),
            );
    }
}
